// RTF → plain-text flattener for legacy single-font body fields stored as RTF
// (e.g. dbo.Accrediation.Text — the accreditation release statement). This is a
// faithful TypeScript port of the backend
// CoverPageAllDataProvider.RtfToPlainText used to render the SAME statement on
// the report cover page, so the detail view and the report agree. It strips RTF
// control words / header groups and keeps paragraph breaks (\par). Not a general
// RTF parser — scoped to the flat, single-font bodies these records hold.

const SKIP_GROUPS = new Set(["fonttbl", "colortbl", "stylesheet", "info", "pict", "object", "themedata", "datastore"]);

const isLetter = (ch: string | undefined): boolean =>
  ch !== undefined && ((ch >= "A" && ch <= "Z") || (ch >= "a" && ch <= "z"));
const isDigit = (ch: string | undefined): boolean => ch !== undefined && ch >= "0" && ch <= "9";

/**
 * Flatten an RTF string ("{\rtf…}") to readable plain text. Returns the input
 * trimmed when it is already plain text, and '' for null / empty input — never
 * throws, so a malformed value degrades to blank rather than breaking the form.
 */
export function rtfToPlainText(rtf: string | null | undefined): string {
  if (!rtf || rtf.trim().length === 0) return "";
  if (!rtf.trimStart().toLowerCase().startsWith("{\\rtf")) return rtf.trim();

  const len = rtf.length;
  const parts: string[] = [];
  let depth = 0;
  let i = 0;
  let inSkipGroup = false;
  let skipDepth = 0;

  while (i < len) {
    const c = rtf[i];
    if (c === undefined) break; // i < len guarantees this never fires; narrows the type
    if (c === "\\") {
      const n = i + 1 < len ? rtf[i + 1] : "";
      // Escaped literal ( \\ \{ \} ).
      if (n === "\\" || n === "{" || n === "}") {
        if (!inSkipGroup) parts.push(n);
        i += 2;
        continue;
      }
      // Hex escape \'xx — emit the byte as a Latin-1 char (smart quotes etc.).
      if (n === "'" && i + 3 < len) {
        const code = parseInt(rtf.substr(i + 2, 2), 16);
        if (!inSkipGroup && !Number.isNaN(code)) parts.push(String.fromCharCode(code));
        i += 4;
        continue;
      }
      // Ignorable destination \* — skip the whole enclosing group.
      if (n === "*") {
        if (!inSkipGroup) {
          inSkipGroup = true;
          skipDepth = depth;
        }
        i += 2;
        continue;
      }
      let j = i + 1;
      while (j < len && isLetter(rtf[j])) j++;
      const word = rtf.substring(i + 1, j);
      // Numeric parameter (optionally negative).
      let k = j;
      if (k < len && (rtf[k] === "-" || isDigit(rtf[k]))) {
        k++;
        while (k < len && isDigit(rtf[k])) k++;
      }
      if (!inSkipGroup) {
        if (word === "par" || word === "line") parts.push("\n");
        else if (word === "tab") parts.push("\t");
        else if (SKIP_GROUPS.has(word)) {
          inSkipGroup = true;
          skipDepth = depth;
        }
      }
      // Skip an optional single space delimiter after a control word.
      if (k < len && rtf[k] === " ") k++;
      i = k;
      continue;
    }
    if (c === "{") {
      depth++;
      i++;
      continue;
    }
    if (c === "}") {
      depth--;
      if (inSkipGroup && depth < skipDepth) inSkipGroup = false;
      i++;
      continue;
    }
    if (c === "\r" || c === "\n") {
      i++;
      continue;
    } // literal newlines aren't content in RTF
    if (!inSkipGroup) parts.push(c);
    i++;
  }

  // Collapse 3+ blank lines, trim each line, drop leading/trailing blanks.
  const lines = parts.join("").replace(/\r/g, "").split("\n");
  const outLines: string[] = [];
  for (const ln of lines) {
    const t = ln.trim();
    if (t.length === 0 && (outLines.length === 0 || outLines[outLines.length - 1]?.length === 0)) continue;
    outLines.push(t);
  }
  while (outLines.length > 0 && outLines[outLines.length - 1]?.length === 0) outLines.pop();
  return outLines.join("\n").trim();
}
